import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShieldOff } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/data/EmptyState'
import { CaseFilters } from '@/components/mlro/CaseFilters'
import { MlroCasesQueue } from '@/components/mlro/MlroCasesQueue'
import { CreateCaseDialog } from '@/components/mlro/CreateCaseDialog'
import { useRoles } from '@/hooks/useRoles'
import { MLRO_CASE_STATUSES, MLRO_CASE_SEVERITIES } from '@/types/mlro'
import type { MlroCaseListFilters } from '@/types/mlro'
import { searchEnum, searchString } from '@/lib/search-params'

export const Route = createFileRoute('/mlro/cases/')({
    component: MlroCasesPage,
    validateSearch: (search): MlroCaseListFilters => ({
        status: searchEnum(search.status, MLRO_CASE_STATUSES),
        severity: searchEnum(search.severity, MLRO_CASE_SEVERITIES),
        subjectType: searchString(search.subjectType ?? search.subject_type),
        subjectId: searchString(search.subjectId ?? search.subject_id),
    }),
})

function MlroCasesPage() {
    const filters = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { hasAnyRole } = useRoles()
    const [createOpen, setCreateOpen] = useState(false)

    if (!hasAnyRole('mlro')) {
        return (
            <EmptyState
                icon={ShieldOff}
                title="No access"
                description="The MLRO surfaces are restricted to operators with the MLRO role."
            />
        )
    }

    const setFilters = (next: MlroCaseListFilters) => {
        navigate({
            search: () => prune(next),
            replace: true,
        })
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">Cases</h1>
                    <p className="text-muted-foreground text-sm">
                        MLRO investigation case files. Click a row to view notes, attached alerts,
                        and export options.
                    </p>
                </div>
                <Button onClick={() => setCreateOpen(true)}>Open case</Button>
            </div>

            <CaseFilters value={filters} onChange={setFilters} />

            <Card>
                <CardHeader>
                    <CardTitle>Queue</CardTitle>
                </CardHeader>
                <CardContent>
                    <MlroCasesQueue filters={filters} />
                </CardContent>
            </Card>

            <CreateCaseDialog open={createOpen} onOpenChange={setCreateOpen} />
        </div>
    )
}

function prune(filters: MlroCaseListFilters): MlroCaseListFilters {
    const out: MlroCaseListFilters = {}
    if (filters.status) out.status = filters.status
    if (filters.severity) out.severity = filters.severity
    if (filters.subjectType) out.subjectType = filters.subjectType
    if (filters.subjectId) out.subjectId = filters.subjectId
    return out
}
